/**
 * One-time featured-product spotlight.
 *
 * When the admin checks "Feature this product" on a new listing, the
 * product gets `featured = 1`. The next time a subscriber lands on the
 * app, we show them this modal once with the spotlight item, then
 * record dismissal in localStorage keyed by `(userId, productId)` so
 * the same fan never sees the same spotlight twice.
 *
 * Lives in a portal so it survives parent transforms / overflow:hidden.
 */

import React from 'react';
import { createPortal } from 'react-dom';
import './FeaturedSpotlightModal.css';
import { useModalA11y } from '../../hooks/useModalA11y';
import { Product } from '../../context/AppContext';

interface FeaturedSpotlightModalProps {
  product: Product;
  onClose: () => void;
  onView: () => void;
}

function formatPrice(p: number, sale?: number): { display: string; strike?: string } {
  if (typeof sale === 'number' && sale > 0 && sale < p) {
    return { display: `$${sale.toFixed(2)}`, strike: `$${p.toFixed(2)}` };
  }
  return { display: `$${p.toFixed(2)}` };
}

const FeaturedSpotlightModal: React.FC<FeaturedSpotlightModalProps> = ({ product, onClose, onView }) => {
  const dialogRef = useModalA11y({ onEscape: onClose });
  const priceMeta = formatPrice(product.price, product.salePrice);
  const heroImage = product.images?.[0];

  return createPortal((
    <div
      ref={dialogRef}
      className="spotlight-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={`Featured: ${product.name}`}
      tabIndex={-1}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="spotlight-card animate-spotlight">
        <button
          type="button"
          className="spotlight-close"
          onClick={onClose}
          aria-label="Close"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
            <path d="M18 6L6 18M6 6l12 12"/>
          </svg>
        </button>

        <div className="spotlight-hero">
          {heroImage ? (
            <img
              src={heroImage}
              alt={product.name}
              className="spotlight-hero-img"
              onError={(e) => {
                const img = e.target as HTMLImageElement;
                img.style.display = 'none';
              }}
            />
          ) : (
            <div className="spotlight-hero-placeholder" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="40" height="40">
                <rect x="3" y="3" width="18" height="18" rx="2"/>
                <circle cx="8.5" cy="8.5" r="1.5"/>
                <path d="M21 15l-5-5L5 21"/>
              </svg>
            </div>
          )}
          <span className="spotlight-pill">✨ Just dropped</span>
        </div>

        <div className="spotlight-body">
          <h3 className="spotlight-name">{product.name}</h3>
          {product.description && (
            <p className="spotlight-description">{product.description}</p>
          )}
          <div className="spotlight-price-row">
            <span className="spotlight-price">{priceMeta.display}</span>
            {priceMeta.strike && (
              <span className="spotlight-price-strike">{priceMeta.strike}</span>
            )}
          </div>

          <div className="spotlight-actions">
            <button
              type="button"
              className="spotlight-btn spotlight-btn-primary"
              onClick={onView}
            >
              View in Shop
            </button>
            <button
              type="button"
              className="spotlight-btn spotlight-btn-ghost"
              onClick={onClose}
            >
              Maybe later
            </button>
          </div>
        </div>
      </div>
    </div>
  ), document.body);
};

export default FeaturedSpotlightModal;
